import React, { Reducer, useCallback, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next"
import { colors, fonts, glyphs } from "../../../Styles"
import { CoreWidget, StartSectionModal, CalibrateSectionModal, EmptySectionModal } from "../..";
import { AttentionTypes, CreateAttentionStyles, ListStyle_Types } from "../../../Definitions";
import { Graph_QL, useSystem } from "../../../Services";
import { useAppSelector } from "../../../Store";
import { NewButtonList } from "../../BaseComponents/newButton";
import { currentLfViewTypes, ILfContext, LfContext, LfDispatcherT, LfReducer, systemControleTypes } from "../../LocationFeeder/Modals/_Modals/lfPenContext";

interface systemControls_props {
  systemId: number
  focus_view?: boolean
}
/**
 * SystemControls
 * @returns A widget with btns to controle the system.
 */
export default function SystemControls({systemId, focus_view}:systemControls_props) {
  const {t} = useTranslation(); 
  const [isOn, setIsOn] = useState<boolean>(true)
  const [openModal, setOpenModal] = useState<"START" | "CALIBRATE" | "EMPTY" | null >(null)
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);

  const { data: systemsState, emergencyStopSystem } = useSystem(connection); 

const [_state, dispatch] = useReducer<Reducer<ILfContext, LfDispatcherT>>(LfReducer, {
  farm: farm,
  domain: domain,
  connection: connection,
  systemId: systemId,
  selectedPens: [],
  nextFeeding_LocationsToFeed: [],
  section: undefined,
  locationResults: undefined,
  currentPenViewType: currentLfViewTypes.systemControle,
  systemControlePen: {
    systemControleType: undefined
  },
  error: { Elementid: 0, errorText: "" }
} as ILfContext);

const handleCloseModal = () => {
  if (!openModal) return;
  setOpenModal(null);
  dispatch({
    selectedPens: [],
    nextFeeding_LocationsToFeed: [],
    selectedSection: undefined,
    locationResults: undefined,
    systemControlePen: {
      systemControleType: undefined
    },
    error: { Elementid: 0, errorText: "" }
  });
}

  const { attentionType } = useMemo(() => {
    let _attentionType = systemsState[domain]?.data.find((system) => system.system_id === systemId)?.system_attention_type ?? AttentionTypes.good;

    // if (lanLogs && lanLogs.length > 0) {
    //   const hasLevel0 = lanLogs.some(log => log.level === 0);
    //   const hasLevel1 = lanLogs.some(log => log.level === 1);
  
    //   if (hasLevel0) {
    //     _attentionType = AttentionTypes.alert;
    //   } else if (hasLevel1) {
    //     _attentionType = AttentionTypes.warning;
    //   }
    // }

    return {attentionType: _attentionType}
  }, [domain, systemId, systemsState])

  const { textColor, attentionColor, secondaryAttentionText } = useMemo(() => {
    var _Style = CreateAttentionStyles(t, attentionType)
    return _Style
  }, [t, attentionType]);

  const translations = useMemo(() => ({
    systemControls: t("systemControls.general.systemControls", {defaultValue: "System Controls"}),
    systemRunning: attentionType === AttentionTypes.alert || attentionType === AttentionTypes.warning ? `${t("systemControls.general.runningWith")} ${secondaryAttentionText}` : t(`systemControls.states.${AttentionTypes.good}`, {defaultValue: "system running"}),
    stopSystem: t("systemControls.general.stopSystem", {defaultValue: "stop system"}),
    systemOff: t(`systemControls.states.${AttentionTypes.neutral}`, {defaultValue: "system off"}),
    startSystem: t("systemControls.general.startSystem", { defaultValue: "start system" }),
    startSection: t("systemControls.general.startSection", {defaultValue: "Start Section"}),
    calibrateSection: t("systemControls.general.calibrateSection", {defaultValue: "Calibrate Section"}),
    emptySection: t("systemControls.general.emptySection", { defaultValue: "Empty Section" }),
    confirmStop: t("systemControls.general.confirmStop", {defaultValue: "Would you like to proceed the emergency stop of the system?"})
  }),[attentionType, secondaryAttentionText, t])

  const systemPower = useMemo(() => {
    return isOn ? {
      systemPower: translations.systemRunning,
      statusColor: attentionColor,
      buttonText: translations.stopSystem,
      buttonEnhancer: glyphs.On({ size: focus_view ? "40px" : "35px" }),
      textColor: textColor
    } : {
      systemPower: translations.systemOff,
      statusColor: colors.systemRed,
      buttonText: translations.startSystem,
      buttonEnhancer: glyphs.Off({ size: focus_view ? "40px" : "35px" }),
      textColor: textColor
    };
  }, [isOn, translations, attentionColor, textColor, focus_view]);

  const handleButtonClick = useCallback((i: number) => {
    if (i === 0) { setOpenModal("START"); dispatch({ systemControlePen: { systemControleType: systemControleTypes.startSection }})}
    if (i === 1) { setOpenModal("CALIBRATE"); dispatch({ systemControlePen: { systemControleType: systemControleTypes.calibrateSection }})}
    if (i === 2) { setOpenModal("EMPTY"); dispatch({ systemControlePen: { systemControleType: systemControleTypes.emptySection }})}
    if (i === 3) {
    if (isOn) {
      if (window.confirm(translations.confirmStop)) { setIsOn(false); emergencyStopSystem(systemId) }
    } else {
      setIsOn(true);
    }
  }
  }, [isOn, translations.confirmStop, emergencyStopSystem, systemId]);

  const buttonListContents = useMemo(() => ([
    translations.startSection,
    translations.calibrateSection,
    translations.emptySection,
    systemPower.buttonText
  ]), [translations, systemPower.buttonText]);
  
  const buttonListStartEnhancers = useMemo(() => ([
    glyphs.Start({ size: focus_view ? "40px" : "35px" }),
    glyphs.Calibrate({ size: focus_view ? "40px" : "35px" }),
    glyphs.Empty({ size: focus_view ? "40px" : "35px" }),
    systemPower.buttonEnhancer
  ]), [focus_view, systemPower.buttonEnhancer]);

  const buttonListDisabledState = useMemo(() => ([
    isOn ? false : true,
    isOn ? false : true,
    isOn ? false : true,
    false
  ]), [isOn]);
  
  return( <LfContext.Provider value={{ state: _state, dispatch}}>
    <CoreWidget
      header={<p style={{...fonts.header, ...(focus_view ? fonts.fontSize2 : fonts.fontSize3), color: colors.greyLightTertiary}}>{translations.systemControls}</p>}
      bgColor={colors.greyPrimary}
      width={"100%"}
      height={focus_view ? "385px" : "350px"}
      content={
        <div style={{display: "flex", flexDirection: "column", gap: "20px", marginBottom: "20px"}}>
          <div style={{display: "flex", justifyContent: "center", alignItems: "center", gap: "5px"}}>
            <p style={{...(focus_view ? fonts.fontSize3 : fonts.fontSize4), ...fonts.paragraph, color: colors.greyLightTertiary}}>{`${t("systemControls.general.systemState", {defaultValue: "System state"})}:`}</p>
            <div style={{...(focus_view ? fonts.fontSize3 : fonts.fontSize4), ...fonts.header, backgroundColor: systemPower.statusColor, color: systemPower.textColor, padding: "4px 5px", borderRadius: "6px", width: "fit-content"}}>{ systemPower.systemPower }</div>
          </div>
          <NewButtonList
            focus_view={focus_view}
            contents={buttonListContents}
            startEnhancers={buttonListStartEnhancers}
            overrides={{
              baseStyles: {backgroundColor: colors.greySecondary},
              hoverStyles: {backgroundColor: colors.greyDarkSecondary},
              disabledStyles: {opacity: "0.3"}
            }}
            listOverrides={{
              listStyle: ListStyle_Types.vertical,
              listGap: "10px"
            }}
            disabled={buttonListDisabledState}
            onClick={handleButtonClick}
          />
        </div>
      }
    />

    <StartSectionModal isModalOpen={openModal === "START"} onClose={handleCloseModal} focus_view={focus_view}/>
    <CalibrateSectionModal isModalOpen={openModal === "CALIBRATE"} onClose={handleCloseModal} focus_view={focus_view}/>
    <EmptySectionModal isModalOpen={openModal === "EMPTY"} onClose={handleCloseModal} focus_view={focus_view}/>
  </LfContext.Provider> )
}

